/**
 * Triveny.ai — PUBLIC landing (unauthenticated), served at the apex.
 *
 * On load it asks the backend for a PUBLISHED Studio page mapped to this host
 * (GET /api/public/site/home). If one exists, it renders that page via
 * PublicBlockRenderer — so Triveny (and any client) can manage their public
 * site in Design Studio. Until a page is published, it falls back to the
 * built-in cycling hero (TrivenyHero) — the canonical Design/Market/Engage
 * hero. A Studio page that is a single `triveny_hero` block renders the EXACT
 * same TrivenyHero, so a designed home is pixel-identical to this fallback.
 *
 * Hero styling lives in the dedicated home stylesheet src/styles/triveny-home.css
 * (loaded globally in main.tsx).
 */

import { useEffect, useState } from 'react';
import { config } from '../../../config';
import PublicBlockRenderer from './PublicBlockRenderer';
import TrivenyHero from './TrivenyHero';
import { CraftDocRenderer } from '../../modules/web-presence-v2/CraftBuilder';

interface PublicSitePage {
  tenant_slug?: string;
  title?: string;
  meta_title?: string;
  meta_description?: string;
  brand_settings?: Record<string, unknown>;
  design_tokens?: { token_group: string; token_name: string; token_value: string }[];
  content_blocks?: Record<string, unknown>[];
}

export default function TrivenyPublicHome() {
  const [page, setPage] = useState<PublicSitePage | null>(null);
  // A DEPLOYED Web Presence v2 (Craft.js) document for this host, if any. Takes
  // precedence over the flat page + the built-in hero. Only the root path uses
  // the per-tenant 'home' craft doc today.
  const [craftDoc, setCraftDoc] = useState<any | null>(null);
  const [craftBrand, setCraftBrand] = useState<any>({});
  const [craftMeta, setCraftMeta] = useState<any>(undefined);

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const apex = host.replace(/^www\./, '');
  const appOrigin = apex.endsWith('triveny.ai') ? `https://app.${apex}` : '';
  // Already signed in (token present)? Send them straight to the app; otherwise
  // to login. The label flips to match.
  const authed = typeof window !== 'undefined' && !!localStorage.getItem('auth_token');
  const loginUrl = appOrigin
    ? authed
      ? appOrigin
      : `${appOrigin}/login`
    : authed
      ? '/'
      : '/login';
  const loginLabel = authed ? 'Go to app' : 'Log in';
  // "Get started" → self-serve signup on the app host (only when logged out).
  const signupUrl = authed ? '' : appOrigin ? `${appOrigin}/signup` : '/signup';

  // Path-aware: root ('/') → the site's home page; any other path → the
  // published page with that slug (deterministic mapping). If a published
  // Studio page exists we render it; otherwise the cycling hero stays (the
  // common case today returns 404 and we never leave the hero).
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const slug = path.replace(/^\/+|\/+$/g, ''); // '' for root

  useEffect(() => {
    if (!host) return;
    let cancelled = false;
    const base = config.weaveinApi?.baseUrl || '';
    const url = slug
      ? `${base}/api/public/site/page?host=${encodeURIComponent(host)}&slug=${encodeURIComponent(slug)}`
      : `${base}/api/public/site/home?host=${encodeURIComponent(host)}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: PublicSitePage | null) => {
        if (cancelled || !data) return;
        if (Array.isArray(data.content_blocks) && data.content_blocks.length > 0) {
          setPage(data);
          const t = data.meta_title || data.title;
          if (t) document.title = t;
        }
      })
      .catch(() => {
        /* network/404 → keep the hero */
      });
    return () => {
      cancelled = true;
    };
  }, [host, slug]);

  // Deployed Web Presence v2 (Craft.js) page for this host (root only for now).
  useEffect(() => {
    if (!host || slug) return; // craft 'home' doc is the apex page
    let cancelled = false;
    const base = config.weaveinApi?.baseUrl || '';
    fetch(`${base}/api/public/site/craft?host=${encodeURIComponent(host)}&key=home`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (data.document) setCraftDoc(data.document);
        if (data.brand && typeof data.brand === 'object') setCraftBrand(data.brand);
        if (data.meta && typeof data.meta === 'object') setCraftMeta(data.meta);
      })
      .catch(() => {
        /* network/none → fall through to flat page / hero */
      });
    return () => {
      cancelled = true;
    };
  }, [host, slug]);

  // Precedence: deployed v2 (Craft) page → flat Studio page → built-in hero.
  if (craftDoc) return <CraftDocRenderer document={craftDoc} brand={craftBrand} meta={craftMeta} />;
  if (page) return <PublicBlockRenderer page={page} loginUrl={loginUrl} loginLabel={loginLabel} signupUrl={signupUrl} />;
  return <TrivenyHero loginUrl={loginUrl} loginLabel={loginLabel} signupUrl={signupUrl} />;
}
